import { useMemo, useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

// ============================================================================
// ExtendOfferButton — Phase F offer flow (pt.32/pt.34).
// Mr. Electric pays commission for licensed techs, hourly for
// helpers/apprentices. Real commission percentages and benefit
// dollar amounts are delivered by Brett/Nathanael directly to the
// candidate, outside the system — this form deliberately captures
// only the STRUCTURE label + dates + optional free-text notes. No
// numeric comp fields anywhere.
// ============================================================================

const COMPENSATION_TYPES = [
  "Commission (Journeyman)",
  "Commission (Master)",
  "Hourly (Helper/Apprentice)",
];

const DEFAULT_OFFER_NOTES = (
  "Compensation structure: commission-based per Mr. Electric "
  + "Commission Payment System. Specific rate, benefits package, "
  + "and full terms will be provided directly at signing."
);

function _addBusinessDays(startISO, days) {
  // Returns ISO YYYY-MM-DD of `startISO + `days` business days.
  // Days = 5-day mon-fri week. Matches the backend default.
  const d = new Date(startISO);
  let added = 0;
  while (added < days) {
    d.setUTCDate(d.getUTCDate() + 1);
    const wd = d.getUTCDay(); // 0 Sun … 6 Sat
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d.toISOString().slice(0, 10);
}

export default function ExtendOfferButton({ candidate, onExtended }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const todayISO = new Date().toISOString().slice(0, 10);
  const defExpiration = useMemo(() => _addBusinessDays(todayISO, 5), [todayISO]);

  const [form, setForm] = useState({
    position_title: candidate?.position_title || "",
    compensation_type: "",
    start_date: "",
    expiration_date: defExpiration,
    notes: DEFAULT_OFFER_NOTES,
  });

  const canSubmit = (
    !saving
    && COMPENSATION_TYPES.includes(form.compensation_type)
    && !!form.start_date
    && !!form.expiration_date
  );

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        compensation_type: form.compensation_type,
        start_date: form.start_date,
        expiration_date: form.expiration_date,
        notes: form.notes,
      };
      const { data } = await api.post(
        `/hiring/candidates/${candidate.id}/extend-offer`,
        body,
      );
      toast.success(
        `Offer extended — ${data.offer.compensation_type}, `
        + `starting ${data.offer.start_date}`,
      );
      setOpen(false);
      onExtended?.(data);
    } catch (e) {
      toast.error(extractApiError(e) || "Extend offer failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        data-testid="hiring-candidate-extend-offer-btn"
      >
        <Sparkles className="w-4 h-4 mr-1.5" /> Extend offer
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="hiring-candidate-extend-offer-modal"
        >
          <DialogHeader>
            <DialogTitle>Extend offer — {candidate?.contact?.name}</DialogTitle>
          </DialogHeader>

          <div className="text-xs text-[#4b5563] bg-[#f3f4f6] border border-[#e5e7eb] rounded p-3 mb-2">
            Writes the offer record + sends the <code>stage_5_offer</code> email
            to the candidate + promotes them to Stage 5. Real commission
            percentages and benefit numbers are delivered by you or Nathanael
            directly — never entered here.
          </div>

          <div className="grid gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                Position title
              </label>
              <Input
                value={form.position_title}
                disabled
                data-testid="hiring-offer-position-title"
              />
              <div className="text-[11px] text-[#4b5563] mt-1">
                Pre-filled from the candidate — edit on the candidate record if needed.
              </div>
            </div>

            <div>
              <label
                htmlFor="hiring-offer-compensation-type"
                className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1"
              >
                Compensation type
              </label>
              <select
                id="hiring-offer-compensation-type"
                value={form.compensation_type}
                onChange={(e) => setForm({ ...form, compensation_type: e.target.value })}
                className="w-full border border-[#e5e7eb] rounded px-3 py-2 text-sm bg-white"
                data-testid="hiring-offer-compensation-type"
              >
                <option value="" disabled>Select compensation structure…</option>
                {COMPENSATION_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <div className="text-[11px] text-[#4b5563] mt-1">
                Structure only — no rates or dollar amounts captured here.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                  Start date
                </label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="hiring-offer-start-date"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                  Expiration date
                </label>
                <Input
                  type="date"
                  value={form.expiration_date}
                  onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                  data-testid="hiring-offer-expiration-date"
                />
                <div className="text-[11px] text-[#4b5563] mt-1">
                  Defaults to +5 business days.
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                Notes / terms
              </label>
              <Textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="hiring-offer-notes"
              />
              <div className="text-[11px] text-[#4b5563] mt-1">
                Pre-filled with the standard MRE language. Editable, but do NOT
                paste real commission percentages or benefit dollar amounts.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="hiring-offer-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="hiring-offer-submit"
            >
              {saving ? "Extending…" : "Extend offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
